import {
    Component,
    Input,
    Output,
    ElementRef,
    OnInit,
    AfterViewInit,
    TemplateRef,
    EventEmitter,
    ViewChild,
    OnChanges,
    OnDestroy,
    ChangeDetectionStrategy,
} from "@angular/core";
import moment from "moment";

import { Validity } from "../store/validity/state";
import { assert } from "../utils/debug";
import { isNullUndefinedOrEmptyString } from "../utils/utils";
import { Helpers } from "../utils/browser";
import { NotificationService } from "../services/notification.service";

declare let $: any;

@Component({
    selector: "zbd-input",
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div
            [ngSwitch]="type"
            class="root"
            [ngClass]="{ 'has-error': invalid }"
        >
            <label *ngIf="labelBefore" [attr.for]="ctrlId">
                {{ label
                }}<span *ngIf="required" class="star-required"> *</span>
            </label>
            <div *ngIf="labelBefore" class="pull-right label-addon-container">
                <ng-container [ngTemplateOutlet]="labelAddon"></ng-container>
            </div>
            <input
                *ngSwitchCase="'text'"
                #control
                [attr.id]="ctrlId"
                type="text"
                class="form-control"
                [ngClass]="{ dirty: dirty, 'has-error-hint': hasErrorHint }"
                (input)="onValueChanged($event)"
                (focus)="onFocus($event)"
                (blur)="onBlur($event)"
                [attr.placeholder]="getPlaceholder()"
                [attr.readonly]="isReadonly('readonly')"
            />
            <textarea
                *ngSwitchCase="'textarea'"
                #control
                [attr.id]="ctrlId"
                class="form-control"
                [ngClass]="{
                    dirty: dirty,
                    'has-error-hint': hasErrorHint,
                    'disallow-resize': textareaAutogrow,
                }"
                (input)="onValueChanged($event)"
                (focus)="onFocus($event)"
                (blur)="onBlur($event)"
                (change)="requestTextareaResize()"
                (keydown)="requestTextareaResize()"
                (paste)="requestTextareaResize()"
                (drop)="requestTextareaResize()"
                (cut)="requestTextareaResize()"
                (window:resize)="requestTextareaResize()"
                [attr.readonly]="isReadonly('readonly')"
            ></textarea>
            <input
                *ngSwitchCase="'checkbox'"
                #control
                [attr.id]="ctrlId"
                type="checkbox"
                class="form-control"
                [ngClass]="{ dirty: dirty, 'has-error-hint': hasErrorHint }"
                (change)="onCheckedChanged($event)"
                [attr.readonly]="isReadonly('readonly')"
            />
            <input
                *ngSwitchCase="'radio'"
                #control
                [attr.id]="ctrlId"
                type="radio"
                class="form-control"
                [ngClass]="{ dirty: dirty, 'has-error-hint': hasErrorHint }"
                (change)="onOptionChanged($event)"
                [attr.readonly]="isReadonly('readonly')"
            />
            <select
                *ngSwitchCase="'select'"
                #control
                [attr.id]="ctrlId"
                class="form-control"
                [ngClass]="{ dirty: dirty, 'has-error-hint': hasErrorHint }"
                (change)="onValueChanged($event)"
                [attr.disabled]="isReadonly('disabled')"
            >
                <ng-content></ng-content>
            </select>
            <div *ngSwitchCase="'date'" class="input-group date">
                <input
                    #control
                    [attr.id]="ctrlId"
                    type="text"
                    class="form-control input-with-unit"
                    [ngClass]="{ dirty: dirty, 'has-error-hint': hasErrorHint }"
                    (change)="onValueChanged($event)"
                    [attr.readonly]="isReadonly('readonly')"
                />
                <span class="input-group-addon"
                    ><span class="icon-date input-group-addon-icon"></span
                ></span>
            </div>
            <label
                *ngIf="labelAfter"
                [attr.for]="ctrlId"
                [ngClass]="{ 'touch-checkbox': appearance === 'touch' }"
            >
                {{ label ? label : "&nbsp;"
                }}<span *ngIf="required && label" class="star-required">
                    *</span
                >
            </label>
            <div *ngIf="invalid" class="error-note">
                {{ message }}
            </div>
        </div>
    `,
    styles: [
        `
            div.root {
                position: relative; /* for datepicker */
            }
            select.form-control {
                height: 40px;
                padding: 6px 6px;
            }
            .disallow-resize {
                resize: none;
                overflow: hidden;
            }
            /* extra large checkboxes
        input[type="checkbox"] + label.touch-checkbox, input[type="radio"] + label.touch-checkbox {
            background-size: 26px 26px;
            line-height: 26px;
            padding-left: 35px;
        }*/
            .label-addon-container {
                position: relative;
                bottom: 5px;
            }
        `,
    ],
})
export class ZbdInputComponent
    implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
    @Input() type: ZbdInputType;
    @Input() name: string;
    @Input() label: string;
    @Input() inlineLabel = false;
    @Input() rows = 2;
    @Input() entity: any;
    @Input() property: string;
    @Input() value: any;
    @Input() updateFn: (
        entity: string,
        property: string,
        value: any,
        flush: boolean,
    ) => void;
    @Input() validity: Validity;
    @Input() locked: boolean;
    @Input("label-addon") labelAddon: TemplateRef<any> = null;
    @Input() appearance: "touch" | "regular" = "regular";
    @Input("autogrow") textareaAutogrow = false;
    @Input("focused-rows") textareaFocusedRows: number = null;
    @Input("rel-thres-year") relThresYear: number = null;
    @Input() securityFilter: "full" | "light" | "none" = "full";

    @Output("value-change") valueChange = new EventEmitter<string | boolean>();

    @ViewChild("control") control: ElementRef;

    invalid = false;
    message = null;
    required = false;
    focused = false;

    dirty = false;
    hasErrorHint = false;

    forceUnlocked = false;

    dpOptions = {
        format: "DD.MM.YYYY",
        locale: "de",
        viewMode: "years",
        icons: {
            // previous: 'icon-link-previous',
            // next: 'icon-arrow-breadcrumb'
        },
        keyBinds: {
            // Allows cursor movement via arrow keys
            left: null,
            right: null,
        },
        useCurrent: false,
        keepInvalid: true,
    };
    private static curCentury = new Date().getFullYear().toString().slice(0, 2);

    private lastCursorPosition = 0;
    private nextTextValue: string;

    private animationFrameCallbackId = null;
    private resizeObserver: ResizeObserver = null;

    protected serial = nextSerial++;

    constructor(private notify: NotificationService) {}

    ngOnInit() {
        assert(
            this.entity ? this.property : !this.property,
            `Input ${this.controlDesc} needs to have all or none of 'entity' and 'property'.`,
        );
        if (this.validity) {
            if (!this.name) {
                assert(
                    false,
                    `Input ${this.controlDesc} has a validity, but no name.`,
                );
            } else if (!this.validity[this.name]) {
                assert(false, `Input ${this.controlDesc} has no validator.`);
            }
        }
    }

    ngAfterViewInit() {
        if (this.type === "date") {
            const self = this;
            const datepicker$ = $(this.control.nativeElement).parent();
            datepicker$.datetimepicker(this.dpOptions);
            datepicker$.on("dp.change", (e) => {
                if (e.date && !e.date.isValid()) {
                    return;
                }
                const forView = e.date ? e.date.format("DD.MM.YYYY") : null;
                const forStore = e.date ? e.date.format("YYYY-MM-DD") : null;
                datepicker$.val(forView);
                this.changed(forStore);
            });

            datepicker$.on("dp.show", function () {
                self.alignWidget();
                $(window).on("resize", self.alignWidget);
                $(this)
                    .data("DateTimePicker")
                    .keyBinds($.fn.datetimepicker.defaults.keyBinds);
            });

            datepicker$.on("dp.hide", function () {
                $(this).data("DateTimePicker").keyBinds({
                    left: null,
                    right: null,
                });
                $(window).off("resize", self.alignWidget);
                /**
                 * we don't want the input field to be blurred after using datetimepicker...
                 * The reason: after selecting the date, we want to be able to press enter in order to submit the form.
                 */
                refocusOnce(self.control.nativeElement);
                function refocusOnce(elem) {
                    function refocusAndOff() {
                        $(elem).off("blur", refocusAndOff);
                        elem.focus();
                    }
                    $(elem).on("blur", refocusAndOff);
                }
            });
        }
        this.updateView();
    }

    private alignWidget() {
        let widget = $(".bootstrap-datetimepicker-widget");
        if (widget[0].getBoundingClientRect().left < 0) {
            widget.css("left", 0);
            widget.removeClass("pull-right");
        }
        widget = null;
    }

    ngOnChanges() {
        if (this.validity && this.validity[this.name]) {
            this.hasErrorHint = this.invalid || !this.validity[this.name].valid;
            this.required = this.validity[this.name].required;
            if (this.dirty) {
                //this.invalid = !this.validity[this.name].valid
                //this.message = this.validity[this.name].message
            }
        }
        this.dirty = false;
        if (!this.focused) {
            this.updateView();
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            // not disconnecting the observer might freeze IE
            this.resizeObserver.disconnect();
        }
        if (this.animationFrameCallbackId) {
            // not canceling the callback might create a new observer which might freeze IE
            cancelAnimationFrame(this.animationFrameCallbackId);
        }
        $(window).off("resize", this.alignWidget);
    }

    get textValue(): string {
        return this.entity
            ? !isNullUndefinedOrEmptyString(this.entity[this.property])
                ? this.entity[this.property]
                : ""
            : this.value;
    }

    get checkedValue(): boolean {
        let checked: boolean;
        if (this.entity) {
            checked = this.entity[this.property] == this.value;
        } else {
            checked = this.value;
        }
        return checked;
    }

    get controlDesc(): string {
        let path = "";
        if (this.name) {
            path += `'${this.name}'`;
        }
        if (this.entity) {
            if (path) path += " for ";
            if (this.entity && this.entity["@path"]) {
                path += `${this.entity["@path"].join(".")}.`;
            }
            if (this.property) {
                path += this.property;
            }
        }
        return path;
    }

    get ctrlId() {
        return `${this.name}-${this.serial}`;
    }

    get labelBefore(): boolean {
        return (
            this.type !== "checkbox" &&
            this.type !== "radio" &&
            (this.type !== "text" || !this.inlineLabel) &&
            this.label &&
            this.label.length > 0
        );
    }

    get labelAfter(): boolean {
        return this.type === "checkbox" || this.type === "radio";
    }

    onValueChanged = (event) => {
        let rawValue = event.target.value;
        let cleanedValue =
            typeof rawValue === "string"
                ? rawValue
                      .replace(
                          /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\uE000-\uF8FF]/g,
                          "",
                      )
                      .replace(/[\t\u00A0]/g, " ")
                : rawValue; // Remove control characters
        if (typeof cleanedValue === "string") {
            cleanedValue = this.filterInputString(cleanedValue);
        }
        if (rawValue != cleanedValue) {
            rawValue = cleanedValue;
            event.target.value = cleanedValue;
            this.notify.warning(
                "Eingabe",
                "Der eingegebene Text enthielt ungültige Zeichen, die in der Folgeverarbeitung zu Fehlern geführt hätten. Diese Zeichen wurden korrigiert.\n" +
                    "Bitte prüfen Sie den aktuellen Text, ob der Inhalt dem gewünschten Inhalt entspricht.",
            );
        }
        const value = this.getValue(rawValue);
        if (this.type === "date" && rawValue !== value) {
            // Immediatly write interpreted date back. Otherwise, the date will show the prior input
            const dateStr = value as string;
            if (!rawValue) {
                event.target.value = "";
            } else if (dateStr.includes(".")) {
                event.target.value = dateStr;
                this.invalid = true;
                this.message = "Das eingegebene Datum ist ungültig";
                return;
            } else {
                event.target.value = moment(dateStr).format("DD.MM.YYYY");
            }
        }
        if (this.type !== "text" && this.type !== "textarea") {
            this.changed(value);
        } else {
            this.dirty = true;
            this.nextTextValue = value as string;
        }
    };

    onCheckedChanged = (event) => {
        this.changed(event.target.checked);
    };

    onOptionChanged = (event) => {
        if (event.target.checked) {
            this.changed(this.value);
        }
    };

    changed(value) {
        this.invalid = false;
        setTimeout(() => {
            this.valueChange.next(value);
            this.dirty = true;
            if (this.updateFn && value !== undefined) {
                const flush =
                    this.type !== "text" &&
                    this.type !== "textarea" &&
                    this.type !== "date";
                this.updateFn(this.entity, this.property, value, flush);
            }
        }, 1);
    }

    getValue(rawValue: string): string | boolean {
        switch (this.type) {
            case "text":
            case "textarea":
                return rawValue;
            case "checkbox":
            case "radio":
                switch (rawValue) {
                    case "true":
                    case "on":
                        return true;
                    case "false":
                    case "off":
                        return false;
                    default:
                        assert(
                            false,
                            `Unhandled ${this.type} value: "${rawValue}"`,
                        );
                        return undefined;
                }
            case "select":
                return rawValue !== "" ? rawValue : null;
            case "date":
                if (!rawValue) {
                    return null;
                }
                const formattedDate = this.parseDateInputString(rawValue);
                const parsedDate = moment(formattedDate, "DD.MM.YYYY");
                return parsedDate.isValid()
                    ? parsedDate.format("YYYY-MM-DD")
                    : formattedDate;
            default:
                assert(false, `Unknown zbd-input type: "${this.type}"`);
                return undefined;
        }
    }

    updateView(): void {
        if (!this.control) {
            return;
        }
        switch (this.type) {
            case "text":
            case "select":
                this.control.nativeElement.value = this.textValue;
                break;
            case "textarea":
                this.control.nativeElement.value = this.textValue;
                this.requestTextareaResize();
                break;
            case "checkbox":
                this.control.nativeElement.checked = this.entity
                    ? this.entity[this.property]
                    : this.value;
                break;
            case "radio":
                this.control.nativeElement.checked = this.checkedValue;
            case "date":
                this.control.nativeElement.value = this.fromDbDate(
                    this.entity ? this.entity[this.property] : this.value,
                );
                break;
            default:
                assert(false, `Unknown zbd-input type: "${this.type}"`);
                return undefined;
        }
    }

    onFocus(event: FocusEvent) {
        this.focused = true;
        if (this.type === "textarea") {
            this.requestTextareaResize();
        }
    }

    onBlur(event: FocusEvent) {
        if (this.type === "text" || this.type === "textarea") {
            this.changed(this.nextTextValue);
        }
        this.focused = false;
        this.lastCursorPosition = this.control.nativeElement.selectionStart;

        // this.updateView()
        if (this.type === "textarea") {
            this.requestTextareaResize();
        }
    }

    asString(value: any): string {
        if (typeof value === "string") {
            return value;
        } else {
            return value.toString();
        }
    }

    isReadonly(trueValue: string) {
        if (
            this.validity &&
            this.validity[this.name] &&
            this.validity[this.name].locked
        ) {
            return trueValue;
        }
        if (this.forceUnlocked) {
            return null;
        }
        if (this.updateFn == null) {
            return trueValue;
        }
        if (
            this.validity &&
            this.validity[this.name] &&
            !this.validity[this.name].valid
        ) {
            this.forceUnlocked = true;
            return null;
        }
        if (this.locked) {
            return trueValue;
        }
        return null;
    }

    getPlaceholder(): string {
        if (this.inlineLabel) {
            return this.required ? `${this.label} *` : this.label;
        } else {
            return undefined;
        }
    }

    // TODO: Should convert between string and Date
    fromDbDate(value: string): string {
        if (!value) {
            return null;
        }
        return moment(value).format("DD.MM.YYYY");
    }

    // TODO: Should convert between string and Date
    toDbDate(value: string): string {
        if (!value) {
            return null;
        }
        return moment(value, "DD.MM.YYYY").format("YYYY-MM-DD");
    }

    public paste(text: string): void {
        assert(
            this.type === "text" || this.type === "textarea",
            "Can only paste into text inputs.",
        );
        const el = this.control.nativeElement as
            | HTMLInputElement
            | HTMLTextAreaElement;
        const value = el.value as string;

        const cursorPosition = this.focused
            ? el.selectionStart
            : this.lastCursorPosition;

        if (cursorPosition >= 0) {
            // search for ... or …
            function replaceDotsAtCursor(
                text: string,
                cursorPosition: number,
                replacement: string,
            ): { updatedText: string; newCursorPosition: number } {
                const checkAndReplace = (
                    offset: number,
                    matchLength: number,
                ): { updatedText: string; newCursorPosition: number } => {
                    const start = offset;
                    const end = start + matchLength;
                    const endslice = text.slice(end).trim();
                    const updatedText = [
                        `${text.slice(0, start).trim()} `,
                        replacement.trim(),
                        endslice.startsWith(",") ? endslice : ` ${endslice}`,
                    ]
                        .filter((t) => !!t)
                        .join("");
                    return {
                        updatedText,

                        newCursorPosition: start + replacement.length + 1,
                    };
                };

                const start = Math.max(0, cursorPosition - 3);
                const end = Math.min(text.length, cursorPosition + 3);
                const surroundingText = text.substring(start, end);

                // Combined check for "..." and "…".
                const patterns = ["...", "…"];
                // check if any of the patterns applies
                const patternIndices = patterns.map(
                    (pattern) =>
                        [pattern, surroundingText.indexOf(pattern)] as const,
                );
                // any index > -1?
                const matchPattern = patternIndices.find(
                    ([pattern, result]) => result > -1,
                );
                if (matchPattern) {
                    return checkAndReplace(
                        start + matchPattern[1],
                        matchPattern[0].length,
                    );
                } else {
                    return checkAndReplace(cursorPosition, 1);
                }
            }
            const { updatedText, newCursorPosition } = replaceDotsAtCursor(
                value,
                cursorPosition,
                text,
            );
            el.value = updatedText;
            el.focus();
            el.setSelectionRange(newCursorPosition, newCursorPosition);
        } else {
            el.value = [value.trim(), text.trim()].join(" ");
        }
        this.requestTextareaResize();
        this.nextTextValue = el.value;
        this.changed(el.value);
    }

    requestTextareaResize() {
        if (this.animationFrameCallbackId !== null) return;
        this.animationFrameCallbackId = window.requestAnimationFrame(() => {
            this.animationFrameCallbackId = null;
            this.resizeTextarea();
        });
    }

    resizeTextarea() {
        const el = this.control && this.control.nativeElement;
        if (!el) return;

        // iOS safari & IE scroll to the top when setting the row attribute of the textarea
        // remember scroll position and restore it later
        // this only happens when ZBD is embedded inside of WebLife
        const scrollingElement =
            document.scrollingElement || document.documentElement;
        const scrollPosition = Math.max(
            scrollingElement.scrollTop,
            window.pageYOffset,
        );

        let computedStyle = null;
        let autogrowRows = 0;
        if (this.textareaAutogrow) {
            if (Helpers.isInternetExplorer()) {
                // IE sets the height of the textarea not according to the rows and line-height
                // therefore the height has to be calculated and set manually
                // set height back to auto for calculations
                el.style.height = "auto";
            }
            el.rows = 1;
            computedStyle = window.getComputedStyle(el);
            const padding =
                parseFloat(computedStyle.paddingTop) +
                parseFloat(computedStyle.paddingBottom);
            const lineHeight = parseFloat(computedStyle.lineHeight);
            autogrowRows = (el.scrollHeight - padding) / lineHeight;
            autogrowRows = Math.floor(autogrowRows + 0.5); // fix rounding errors round down for decimals below .5, round up for decimals above .5
        }

        if (isNaN(autogrowRows) || autogrowRows < 1) {
            // sometimes the DOM element will be present, but not mounted to the DOM
            // listen for resize to detect a mount to the DOM and trigger another recalculation
            if (this.resizeObserver === null) {
                this.resizeObserver = new ResizeObserver(
                    (entries, observer) => {
                        observer.disconnect();
                        this.resizeObserver = null;
                        this.requestTextareaResize();
                    },
                );
                this.resizeObserver.observe(el);
            }

            autogrowRows = 0;
        }

        const useFocusedRows =
            (this.focused &&
                (!this.isReadonly("readonly") || !this.textareaAutogrow)) ||
            (this.textValue && !this.textareaAutogrow);
        const staticRows =
            useFocusedRows && this.textareaFocusedRows
                ? this.textareaFocusedRows
                : this.rows;

        const newRows = Math.max(staticRows, autogrowRows);
        if (el.rows !== newRows) {
            el.rows = newRows;
        }

        if (Helpers.isInternetExplorer()) {
            // IE sets the height of the textarea not according to the rows and line-height
            // therefore the height has to be calculated and set manually
            if (!computedStyle) {
                computedStyle = window.getComputedStyle(el);
            }
            const padding =
                parseFloat(computedStyle.paddingTop) +
                parseFloat(computedStyle.paddingBottom);
            const lineHeight = parseFloat(computedStyle.lineHeight);
            el.style.height = newRows * lineHeight + padding + "px";
        }

        scrollingElement.scrollTop = scrollPosition;
    }

    private filterInputString(rawValue: string): string {
        if (!rawValue || rawValue === "") {
            return rawValue;
        }
        let filteredValue = rawValue;
        if (this.securityFilter === "full") {
            filteredValue = filteredValue.replace(/[&<>"';]/gi, "");
        } else if (this.securityFilter === "light") {
            const invalidStrs = ["<script>", "</script>"];
            invalidStrs.forEach((invalidStr) => {
                const invalidStrIndex = filteredValue
                    .toLowerCase()
                    .indexOf(invalidStr.toLowerCase());
                if (invalidStrIndex !== -1) {
                    // Filter invalid strs
                    filteredValue = filteredValue.replace(
                        filteredValue.substring(
                            invalidStrIndex,
                            invalidStrIndex + invalidStr.length,
                        ),
                        "",
                    );
                }
            });
        }
        return filteredValue;
    }

    private parseDateInputString(rawDateStr) {
        let date = moment(rawDateStr, "DD.MM.YYYY");
        if (!date.isValid()) {
            date = moment(rawDateStr);
        }
        const inputWithoutSeparators = rawDateStr.replace(/\./g, "");
        if (!isFinite(inputWithoutSeparators)) {
            return undefined;
        }

        if (inputWithoutSeparators.length === 4) {
            return this.convertToDateStr(
                {
                    day: { start: 0, length: 1 },
                    month: { start: 1, length: 1 },
                    year: { start: 2 },
                },
                inputWithoutSeparators,
                this.createDateStrWithThresholdedCentury.bind(this),
            );
        } else if (inputWithoutSeparators.length === 5) {
            return this.convertToDateStr(
                {
                    day: { start: 0, length: 2 },
                    month: { start: 2, length: 1 },
                    year: { start: 3 },
                },
                inputWithoutSeparators,
                this.createDateStrWithThresholdedCentury.bind(this),
            );
        } else if (inputWithoutSeparators.length === 6) {
            const monthRange = { start: 2, length: 2 };
            const monthStr = this.crop(monthRange, inputWithoutSeparators);

            if (Number(monthStr) > 12) {
                return this.convertToDateStr(
                    {
                        day: { start: 0, length: 1 },
                        month: { start: 1, length: 1 },
                        year: { start: 2 },
                    },
                    inputWithoutSeparators,
                    this.createDateStr.bind(this),
                );
            } else {
                return this.convertToDateStr(
                    {
                        day: { start: 0, length: 2 },
                        month: monthRange,
                        year: { start: 4 },
                    },
                    inputWithoutSeparators,
                    this.createDateStrWithThresholdedCentury.bind(this),
                );
            }
        } else {
            if (!date.isValid()) {
                date = moment(rawDateStr, "DDMMYYYY");
                if (!date.isValid()) {
                    return rawDateStr;
                }
            }
            // date._i might contain the old (previous) date string if the date was selected by the picker
            // -> if we didn't manipulate, return the string representation of the new date
            return date.format("DD.MM.YYYY");
        }
    }

    private convertToDateStr(indexes, input, dateStrFun) {
        const day = this.crop(indexes.day, input);
        const month = this.crop(indexes.month, input);
        const year = this.crop(indexes.year, input);

        return dateStrFun(day, month, year);
    }

    private createDateStr(day, month, year) {
        return day + "." + month + "." + year;
    }

    private createDateStrWithThresholdedCentury(day, month, year) {
        let targetYear = Number(ZbdInputComponent.curCentury + year);
        if (this.relThresYear || this.relThresYear == 0) {
            const thresYear = new Date().getFullYear() - this.relThresYear;
            if (targetYear > thresYear) {
                targetYear -= 100;
            }
        }
        return this.createDateStr(day, month, targetYear);
    }

    private crop(range, input) {
        if (range.start !== undefined) {
            if (range.length !== undefined) {
                return input.substr(range.start, range.length);
            } else {
                return input.substr(range.start);
            }
        }
    }
}

export type ZbdInputType =
    | "text"
    | "textarea"
    | "checkbox"
    | "radio"
    | "select"
    | "date";

let nextSerial = 0;
